import katex from 'katex'

// ── Math nodes are typeset again after sanitising: keep in sync with
//    progsity-admin/src/lib/render-math.ts
//
// The editor saves a formula as `<span data-type="math-inline" data-latex="…">`
// (or `div[data-type="math-block"]`) holding KaTeX's HTML. KaTeX positions
// superscripts and spaces operators with inline `style`, and every sanitiser
// copy drops `style` on KaTeX's spans, so a saved `-10^{18}` read back with its
// 18 below the line and `1 \leq T` with no space. `data-latex` survives
// sanitising and carries the whole formula, so the stored KaTeX HTML is thrown
// away and typeset again here, after the sanitiser has run. KaTeX escapes its
// input and, with `trust` off (the default), emits no links, scripts or
// user-chosen styles, so its output needs no second pass.
// ─────────────────────────────────────────────────────────────────────────

const MATH_OPEN = /<(span|div)\b[^>]*\bdata-type="math-(inline|block)"[^>]*>/g
const LATEX_ATTR = /\bdata-latex="([^"]*)"/

function decodeAttr(value: string): string {
  return value
    .replace(/&#x([0-9a-f]+);/gi, (_, hex: string) => String.fromCodePoint(parseInt(hex, 16)))
    .replace(/&#(\d+);/g, (_, dec: string) => String.fromCodePoint(Number(dec)))
    .replace(/&quot;/g, '"')
    .replace(/&#39;|&apos;/g, "'")
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
}

function escapeText(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

/** The index just past the tag that closes the element opened before `from`, or -1. */
function findClose(html: string, tag: string, from: number): number {
  const tags = new RegExp(`<(/?)${tag}\\b[^>]*>`, 'gi')
  tags.lastIndex = from
  let depth = 1
  for (let t = tags.exec(html); t; t = tags.exec(html)) {
    if (t[1]) depth -= 1
    else if (!t[0].endsWith('/>')) depth += 1
    if (depth === 0) return tags.lastIndex
  }
  return -1
}

function typeset(latex: string, displayMode: boolean): string {
  try {
    return katex.renderToString(latex, { displayMode, throwOnError: true })
  } catch {
    // The editor's own fallback for a formula KaTeX cannot parse.
    return `<code class="math-error">${escapeText(latex)}</code>`
  }
}

/** Typesets every math node in already-sanitised HTML from its `data-latex`. */
export function renderMathNodes(html: string): string {
  if (!html.includes('data-latex')) return html
  let out = ''
  let cursor = 0
  MATH_OPEN.lastIndex = 0
  for (let m = MATH_OPEN.exec(html); m; m = MATH_OPEN.exec(html)) {
    const [openTag, tag, kind] = m
    const latex = LATEX_ATTR.exec(openTag)
    const end = findClose(html, tag, m.index + openTag.length)
    if (!latex || end < 0) continue
    out += html.slice(cursor, m.index) + openTag + typeset(decodeAttr(latex[1]), kind === 'block') + `</${tag}>`
    cursor = end
    MATH_OPEN.lastIndex = end
  }
  return out + html.slice(cursor)
}
